import { groupByDictUnique } from '../../common/utils';
import { Expansion, ExpansionLevelCap, ExpansionPatch, TExpansionInfo } from '../expansion';
import {
  generateGetSeasonBySlug,
  getSeasonByEventId,
  getSortedSeasons,
  mythicScoreUseAlternateDungeon
} from '../mythicPlus';
import { Difficulty, FeatureFlag, RaidDifficulty, UserConnectionType, WoWDifficultyId } from '../sharedTypes';
import { getForcePathsFromRoutes } from './ads';
import { WOWRETAIL_EVENT_CONFIG } from './events';
import { getEventTitleFromSeasonSlug, SEASONS, SEASONS_BY_EXACT_SLUGS, SEASONS_BY_SLUG } from './mythicPlus';
import { QUALIFIED_GUILD_RECRUITMENT_TAGS } from './qualifiedGuildRecruitmentTags';
import { RAID_METADATA } from './raids';
import { WOW_RETAIL_ROUTES } from './routes';
import { IRetailGameConfig } from './types';

const RAID_DIFFICULTIES: Difficulty[] = [
  {
    id: WoWDifficultyId.NORMAL_RAID,
    slug: RaidDifficulty.NORMAL,
    name: 'Normal',
    acronym: 'N',
    maxMembers: 30
  },
  {
    id: WoWDifficultyId.HEROIC_RAID,
    slug: RaidDifficulty.HEROIC,
    name: 'Heroic',
    acronym: 'H',
    maxMembers: 30
  },
  {
    id: WoWDifficultyId.MYTHIC_RAID,
    slug: RaidDifficulty.MYTHIC,
    name: 'Mythic',
    acronym: 'M',
    maxMembers: 20,
    wowDifficultyIds: [WoWDifficultyId.MYTHIC_RAID, WoWDifficultyId.MYTHIC_FLEXIBLE_RAID]
  }
];

export const EXPANSIONS_INFO: TExpansionInfo[] = [
  {
    slug: 'midnight',
    expansionId: Expansion.Midnight,
    name: 'Midnight',
    short_name: 'MN',
    icon: 'inv_misc_token_boost_generic',
    logoPath: '/images/expansion-logos/midnight.png'
  },
  {
    slug: 'the-war-within',
    expansionId: Expansion.TheWarWithin,
    name: 'The War Within',
    short_name: 'TWW',
    icon: 'inv_misc_token_boost_generic',
    logoPath: '/images/expansion-logos/the-war-within.png'
  },
  {
    slug: 'dragonflight',
    expansionId: Expansion.Dragonflight,
    name: 'Dragonflight',
    short_name: 'DF',
    icon: 'inv_misc_token_boost_dragonflight',
    logoPath: '/images/expansion-logos/dragonflight.png'
  },
  {
    slug: 'shadowlands',
    expansionId: Expansion.Shadowlands,
    name: 'Shadowlands',
    short_name: 'SL',
    icon: 'inv_misc_token_boost_shadowlands',
    logoPath: '/images/expansion-logos/shadowlands.png'
  },
  {
    slug: 'battle-for-azeroth',
    expansionId: Expansion.BattleForAzeroth,
    name: 'Battle for Azeroth',
    short_name: 'BFA',
    icon: 'inv_misc_token_boost_bfa',
    logoPath: '/images/expansion-logos/battle-for-azeroth.png'
  },
  {
    slug: 'legion',
    expansionId: Expansion.Legion,
    name: 'Legion',
    short_name: 'Legion',
    icon: 'inv_misc_token_boost_generic',
    logoPath: '/images/expansion-logos/legion.png'
  }
];

const getExpansionById = (id: Expansion) => {
  return EXPANSIONS_INFO.find((expansion) => expansion.expansionId === id)!;
};

const getExpansionBySlug = (slug: string) => {
  return EXPANSIONS_INFO.find((expansion) => expansion.slug === slug)!;
};

const config: IRetailGameConfig = {
  // SiteBackdropPath: '/images/site/20th_anniversary_backdrop.png',

  //
  // Expansion Settings
  //
  CurrentExpansion: Expansion.Midnight,
  NextExpansion: Expansion.Midnight,
  PreviousExpansion: Expansion.TheWarWithin,
  FirstSupportedExpansion: Expansion.Legion,

  //
  // Patch Settings
  //
  CurrentPatch: ExpansionPatch.Patch_1210,
  FirstSupportedPatch: ExpansionPatch.Patch_700,
  PublicTestRealmPatch: ExpansionPatch.Patch_1210,
  BetaPatch: ExpansionPatch.Patch_1200,

  CurrentLevelCap: ExpansionLevelCap.Midnight,
  PreviousLevelCap: ExpansionLevelCap.TheWarWithin,
  MinScanLevel: 10, // TODO: what value do we need here for retail?

  RaidDifficulties: RAID_DIFFICULTIES,

  Raids: RAID_METADATA,
  RaidsBySlug: groupByDictUnique(RAID_METADATA, 'slug'),

  MythicPlusSeasons: SEASONS,
  MythicPlusSeasonsSorted: getSortedSeasons(SEASONS),
  MythicPlusSeasonsBySlug: SEASONS_BY_SLUG,
  MythicPlusSeasonsByExactSlug: SEASONS_BY_EXACT_SLUGS,
  MythicPlusSeasonsByPartitionEventId: getSeasonByEventId(SEASONS),

  getSeasonBySlug: generateGetSeasonBySlug(SEASONS, SEASONS_BY_EXACT_SLUGS),

  Routes: WOW_RETAIL_ROUTES,

  mythicScoreUseAlternateDungeon,
  getEventTitleFromSeasonSlug,

  getForcePathsFromRoutes,

  Expansions: EXPANSIONS_INFO,
  getExpansionById,
  getExpansionBySlug,

  BaselineFeatures: [
    FeatureFlag.AC_QUEUE,
    FeatureFlag.AC_DOCKING,
    FeatureFlag.AC_DOCKING_CHARACTER,
    FeatureFlag.AC_DOCKING_GUILD,
    FeatureFlag.RECRUITMENT,
    FeatureFlag.RECRUITMENT_VIEW,
    FeatureFlag.RECRUITMENT_SEARCH,
    FeatureFlag.RECRUITMENT_CREATE,
    FeatureFlag.MESSAGE_CENTER,
    FeatureFlag.MANAGE_RAID_ENCOUNTER_VIDEOS,
    FeatureFlag.MANAGE_KEYSTONE_RUN_VIDEOS,
    FeatureFlag.SPEC_TALENTS_COMPARE_PAGE
  ],

  UserConnectionTypes: [
    UserConnectionType.DISCORD,
    UserConnectionType.TWITCH,
    UserConnectionType.YOUTUBE,
    UserConnectionType.GOOGLE,
    UserConnectionType.DOUYU
  ],

  ItemSlotGroups: [
    ['head'],
    ['neck'],
    ['shoulder'],
    ['back'],
    ['chest'],
    ['wrist'],
    ['hands'],
    ['waist'],
    ['legs'],
    ['feet'],
    ['finger1'],
    ['finger2'],
    ['trinket1', 'trinket2'],
    ['mainhand'],
    ['offhand']
  ],

  EventConfig: WOWRETAIL_EVENT_CONFIG,

  QualifiedGuildRecruitmentTags: QUALIFIED_GUILD_RECRUITMENT_TAGS
};

export default config;
